import { useState } from "react";
import axios from "axios";
import { Mail, Check, Send } from "lucide-react";
import { toast } from "sonner";
import SubscriberCount from "@/components/site/SubscriberCount";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Newsletter signup widget. Captures email subscriptions for the monthly newsletter.
 *
 * Props:
 *   variant       – "light" (over ivory bg) | "dark" (over charcoal bg)
 *   source        – tracking source (page name)
 *   hideSocialProof – pass true when the parent already renders SubscriberCount
 *                     (e.g. the /newsletter hero, which uses the bigger variant)
 */
const NewsletterSignup = ({ variant = "light", source, hideSocialProof = false }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Por favor, escribe tu email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        name: form.name.trim() || null,
        email: form.email.trim(),
        source: source || null,
      });
      setSent(true);
      toast.success("¡Te he añadido! Revisa tu email en unos minutos.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo suscribir. Inténtalo otra vez.");
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const inputBase = isDark
    ? "bg-white/10 border-ivory/20 text-ivory placeholder-ivory/40 focus:border-terracotta"
    : "bg-white border-sand text-charcoal placeholder-charcoal/40 focus:border-terracotta";
  const labelColor = isDark ? "text-ivory/55" : "text-charcoal/55";
  const ctaClasses = isDark
    ? "bg-terracotta text-charcoal hover:bg-ivory"
    : "bg-charcoal text-ivory hover:bg-terracotta hover:text-charcoal";

  if (sent) {
    return (
      <div
        data-testid={`newsletter-success-${source || "generic"}`}
        className={`rounded-3xl p-8 md:p-10 ${isDark ? "bg-white/[0.04] border border-ivory/10" : "bg-sand/50 border border-sand"}`}
      >
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isDark ? "bg-terracotta/15 text-terracotta" : "bg-olive/15 text-olive"}`}>
          <Check size={26} />
        </div>
        <h3 className={`mt-5 font-serif text-2xl ${isDark ? "text-ivory" : "text-charcoal"}`}>¡Listo, ya estás dentro!</h3>
        <p className={`mt-3 leading-relaxed text-[15px] ${isDark ? "text-ivory/70" : "text-charcoal/70"}`}>
          Te he enviado un email de bienvenida a <strong>{form.email}</strong>. Llega en menos de 5 minutos.
          Si no lo encuentras, revisa tu carpeta de promociones o spam.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`newsletter-form-${source || "generic"}`}
      className={`rounded-3xl p-8 md:p-10 ${isDark ? "bg-white/[0.04] border border-ivory/10" : "bg-sand/50 border border-sand"}`}
    >
      <p className={`text-[11px] tracking-[0.3em] uppercase mb-1 ${isDark ? "text-terracotta" : "text-sage"}`}>
        Newsletter mensual
      </p>
      <h3 className={`font-serif text-2xl md:text-3xl leading-tight ${isDark ? "text-ivory" : "text-charcoal"}`}>
        Una vez al mes, algo que te <span className="italic text-terracotta">sostenga</span>.
      </h3>
      <p className={`mt-3 text-[14.5px] leading-relaxed max-w-md ${isDark ? "text-ivory/70" : "text-charcoal/70"}`}>
        Una reflexión, un recurso útil y una práctica cuerpo-mente. Sin spam. Sin venderte nada.
        Te puedes dar de baja con un clic.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`nl-name-${source || "x"}`} className="sr-only">Nombre</label>
          <input
            id={`nl-name-${source || "x"}`}
            data-testid={`newsletter-name-${source || "generic"}`}
            type="text"
            placeholder="Tu nombre (opcional)"
            value={form.name}
            onChange={update("name")}
            className={`w-full ${inputBase} border rounded-xl px-4 py-3 text-[15px] focus:outline-none transition-colors`}
          />
        </div>
        <div className="relative">
          <Mail size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${isDark ? "text-ivory/40" : "text-charcoal/40"}`} />
          <label htmlFor={`nl-email-${source || "x"}`} className="sr-only">Email</label>
          <input
            id={`nl-email-${source || "x"}`}
            data-testid={`newsletter-email-${source || "generic"}`}
            type="email"
            required
            placeholder="tu@email.com"
            value={form.email}
            onChange={update("email")}
            className={`w-full ${inputBase} border rounded-xl pl-10 pr-4 py-3 text-[15px] focus:outline-none transition-colors`}
          />
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className={`text-[11.5px] ${labelColor} leading-relaxed max-w-xs`}>
          Tus datos solo los uso para enviarte la newsletter. No los compartiré jamás.
        </p>
        <button
          type="submit"
          disabled={loading}
          data-testid={`newsletter-submit-${source || "generic"}`}
          className={`group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium transition-all disabled:opacity-60 ${ctaClasses}`}
        >
          {loading ? "Enviando..." : "Suscribirme"}
          {!loading && <Send size={14} className="transition-transform group-hover:translate-x-0.5" />}
        </button>
      </div>

      {!hideSocialProof && (
        <div className={`mt-5 pt-5 border-t ${isDark ? "border-ivory/10" : "border-sand/70"}`}>
          <SubscriberCount variant="inline" />
        </div>
      )}
    </form>
  );
};

export default NewsletterSignup;
